// GRC data layer - Incidents, Compliance, Audit log.

import { supabase } from '@govern360/shared';

/* ============================== Incidents ============================== */
/* G360_K459 - tenantId is optional so every existing caller behaves as
   before. With it, the incidents of that organisation only (incidents RLS
   lets anyone in two organisations - and staff - read both), and every row:
   paged past PostgREST's 1,000, to 20,000, with capped set beyond that. */
const INC_PAGE = 1000;
const INC_LIMIT = 20000;
export async function listIncidents(tenantId) {
  if (!tenantId) {
    const { data, error } = await supabase
      .from('incidents').select('*').order('created_at', { ascending: false });
    return { data: data || [], error };
  }
  const rows = [];
  for (let from = 0; from < INC_LIMIT; from += INC_PAGE) {
    const { data, error } = await supabase
      .from('incidents').select('*').eq('tenant_id', tenantId)
      .order('created_at', { ascending: false }).order('id', { ascending: true })
      .range(from, from + INC_PAGE - 1);
    if (error) return { data: [], error };
    const got = data || [];
    for (let i = 0; i < got.length; i += 1) rows.push(got[i]);
    if (got.length < INC_PAGE) return { data: rows, error: null, capped: false };
  }
  return { data: rows, error: null, capped: true };
}
export async function getIncident(id) {
  const { data, error } = await supabase.from('incidents').select('*').eq('id', id).maybeSingle();
  return { data, error };
}
export async function listIncidentEvents(incidentId) {
  const { data, error } = await supabase
    .from('incident_events').select('*').eq('incident_id', incidentId).order('created_at');
  return { data: data || [], error };
}
export async function createIncident(tenantId, inc) {
  const { data, error } = await supabase
    .from('incidents').insert({ ...inc, tenant_id: tenantId }).select().single();
  return { data, error };
}
export async function updateIncident(id, updates) {
  const { data, error } = await supabase
    .from('incidents').update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id).select().single();
  return { data, error };
}
export async function addIncidentEvent(tenantId, incidentId, ev) {
  const { data, error } = await supabase
    .from('incident_events').insert({ ...ev, tenant_id: tenantId, incident_id: incidentId }).select().single();
  return { data, error };
}
/* G360_K459 - deleted says how many rows went. RLS turns a refused delete
   into zero rows and no error, which the page used to report as done. */
export async function deleteIncident(id) {
  const { data, error } = await supabase.from('incidents').delete().eq('id', id).select('id');
  return { error, deleted: error ? 0 : (Array.isArray(data) ? data.length : 0) };
}
export async function seedDemoIncidents() {
  const { data, error } = await supabase.rpc('seed_demo_incidents');
  return { count: data, error };
}

/* ============================== Compliance ============================== */
export async function listControls() {
  const { data, error } = await supabase
    .from('compliance_controls').select('*').order('framework_key').order('control_ref');
  return { data: data || [], error };
}
export async function listFrameworkCatalog() {
  const { data, error } = await supabase
    .from('compliance_framework_catalog').select('*').order('sort');
  return { data: data || [], error };
}
/* G360_K502 - both take the organisation on screen. Without it the
   server fell back to the caller's primary organisation, so staff viewing
   a customer enabled or refreshed the wrong one. The argument stays
   optional so an older caller behaves exactly as before. */
export async function enableFrameworks(tenantId) {
  const { data, error } = await supabase.rpc('enable_compliance_frameworks', tenantId ? { p_tenant: tenantId } : {});
  return { count: data, error };
}
export async function refreshCompliance(tenantId) {
  const { data, error } = await supabase.rpc('refresh_compliance', tenantId ? { p_tenant: tenantId } : {});
  return { count: data, error };
}
/* G360_K502 - Govern360's own scope guidance per control, from the
   catalog (0270). Guidance, never evidence. */
export async function listGuidance() {
  const { data, error } = await supabase.rpc('compliance_guidance');
  const m = {};
  (data || []).forEach((r) => { if (r && r.guidance) m[r.framework_key + '|' + r.control_ref] = r.guidance; });
  return { data: m, error };
}
export async function updateControl(id, updates) {
  const { data, error } = await supabase
    .from('compliance_controls').update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id).select().single();
  return { data, error };
}

/* ============================== Audit log ============================== */
/* G360_K430 - tenantId and a list of levels, both optional so every
   existing caller behaves as before.

   tenantId: audit_events RLS lets staff read every tenant's rows (the
   same reason listPolicies filters explicitly - G360_TENANT_SCOPE), so a
   staff user looking at one organization's audit log saw every
   organization's events mixed together. Filtering here is what makes the
   organization switcher mean something on this page. */
export async function listAudit({ level, levels, q, limit = 100, tenantId } = {}) {
  let query = supabase
    .from('audit_events').select('*').order('created_at', { ascending: false }).limit(limit);
  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (Array.isArray(levels) && levels.length) query = query.in('level', levels);
  else if (level && level !== 'all') query = query.eq('level', level);
  const { data, error } = await query;
  let rows = data || [];
  if (q) {
    const needle = q.toLowerCase();
    rows = rows.filter((r) =>
      `${r.event} ${r.actor_email} ${JSON.stringify(r.detail || {})}`.toLowerCase().includes(needle)
    );
  }
  return { data: rows, error };
}

/* How many events there are, not how many were loaded. head: true asks
   for the count alone. A failed count returns count: null - the caller
   must not show it as zero. */
export async function countAudit({ tenantId, levels } = {}) {
  let query = supabase.from('audit_events').select('*', { count: 'exact', head: true });
  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (Array.isArray(levels) && levels.length) query = query.in('level', levels);
  const { count, error } = await query;
  return { count: error ? null : (typeof count === 'number' ? count : null), error };
}

/* ============================== UI constants ============================== */
export const INCIDENT_STATUS = {
  open:          { label: 'Open',          pill: 'p-dang' },
  investigating: { label: 'Investigating', pill: 'p-warn' },
  contained:     { label: 'Contained',     pill: 'p-info' },
  resolved:      { label: 'Resolved',      pill: 'p-succ' },
};
export const SEVERITY = {
  Low:      { dot: 'rd-low' },
  Medium:   { dot: 'rd-medium' },
  High:     { dot: 'rd-high' },
  Critical: { dot: 'rd-critical' },
};
export const CONTROL_STATUS = {
  not_started:    { label: 'Not started',    pill: 'p-gray' },
  in_progress:    { label: 'In progress',    pill: 'p-warn' },
  satisfied:      { label: 'Satisfied',      pill: 'p-succ' },
  not_applicable: { label: 'N/A',            pill: 'p-gray' },
};
